import * as Api from '@ViewModels';
import * as React from 'react';
import { toast } from 'sonner';
import { useEventLogging } from '../../../../models/Logging';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import {
	findPlatformCustomization,
	getTargetedPlatforms,
	isSplitByPlatform,
} from '../../../../models/utils/platformCustomizations';
import { useAIAssistantCreateConversationMutation } from '../../../../queries/AI/useAIAssistantCreateConversationMutation';
import { useMultipleAttachmentsQuery } from '../../../../queries/Attachments/useMultipleAttachmentsQuery';
import { useGetSystemJobQuery } from '../../../../queries/SystemJob';
import { getSystemJobProgressFingerprint, isSystemJobFinished } from '../../../../queries/SystemJob/jobStatus';
import { useJobStallTimeout } from '../../../../queries/SystemJob/useJobStallTimeout';
import { getBrandingProgressText } from '../../../components-v2/UnifiedImageDialog/BulkRebrandingTab/hooks/applyBranding';
import { getBrandingBatchForAttachments } from '../../../components-v2/UnifiedImageDialog/BulkRebrandingTab/hooks/brandingBatch';
import {
	type BrandingRetry,
	BrandingSystemJobResult,
	IMAGE_BRANDING_ASSISTANT_NAME,
	KEPT_IMAGES_UNAVAILABLE_MESSAGE,
	startImageBrandingStream,
} from '../../../components-v2/UnifiedImageDialog/BulkRebrandingTab/hooks/imageBrandingStream';
import type { IEditSocialMediaPostContext } from '../../../containers/socialMedia/EditSocialMediaPost/context';
import type { BrandingTabActivity } from './useBrandingTabIndicator';

const JOB_POLL_INTERVAL_MS = 3000;

type MediaReplacement = (media: readonly Api.IFileAttachment[]) => readonly Api.IFileAttachment[];

export interface IBrandingVersionMedia {
	attachments: readonly Api.IFileAttachment[];
	platform: Api.SocialMediaType | undefined;
}

export const getPostBrandingVersions = (context: IEditSocialMediaPostContext): IBrandingVersionMedia[] => {
	if (!isSplitByPlatform(context.customizeByPlatform, context.postTargets)) {
		return [{ attachments: context.postImages ?? [], platform: undefined }];
	}
	const seedImages = context.splitBaseImages ?? context.postImages ?? [];
	return getTargetedPlatforms(context.postTargets).map(platform => ({
		attachments: findPlatformCustomization(context.platformCustomizations, platform)?.attachments ?? seedImages,
		platform,
	}));
};

interface IBrandingAnchor {
	imageIds: readonly string[];
	jobId?: string;
	templateId?: string;
}

const withoutRejectedJob = (anchor: IBrandingAnchor | undefined, rejectedJobId: string) =>
	anchor?.jobId === rejectedJobId ? { ...anchor, jobId: undefined } : anchor;

interface IBrandingRun {
	anchorBefore?: IBrandingAnchor;
	brandedBySource: Record<string, Api.IFileAttachment>;
	jobId?: string;
	outcome?: { failedImages: number };
	runId: number;
	retry?: BrandingRetry;
	snapshot: readonly IBrandingVersionMedia[];
	sourceIds: string[];
	state: 'starting' | 'running' | 'cancelled' | 'failed' | 'done';
	templateId?: string;
}

const isUntouchedSince = (versions: readonly IBrandingVersionMedia[], run: IBrandingRun) =>
	versions.length === run.snapshot.length &&
	versions.every((version, index) => {
		const before = run.snapshot[index];
		return (
			before.platform === version.platform &&
			version.attachments.length === before.attachments.length &&
			version.attachments.every((attachment, position) => {
				const original = before.attachments[position];
				return attachment.id === original.id || run.brandedBySource[original.id]?.id === attachment.id;
			})
		);
	});

const isRunInFlight = (run: IBrandingRun | undefined) => run?.state === 'starting' || run?.state === 'running';

const getPostWideAttachments = (versions: readonly IBrandingVersionMedia[]) =>
	versions.flatMap(version => [...version.attachments]);

interface IParams {
	impersonationContext?: Api.IImpersonationContext;
	replaceMedia: (replacement: MediaReplacement, platform: Api.SocialMediaType | undefined) => void;
	templateId?: string;
	versions: readonly IBrandingVersionMedia[];
}

export interface IBrandingState {
	allImagesBranded: boolean;
	apply: () => void;
	brandingImageIds: ReadonlySet<string>;
	canApply: boolean;
	canUndo: boolean;
	cancel: () => void;
	failedImages: number;
	hasFailed: boolean;
	isBranding: boolean;
	progressText: string;
	undo: () => void;
}

export function useBrandPostMedia({ impersonationContext, replaceMedia, templateId, versions }: IParams) {
	const logger = useEventLogging('useBrandPostMedia');
	const userSession = useUserSession();
	const [run, setRun] = React.useState<IBrandingRun | undefined>(undefined);
	// Later runs are branded against this job's images so a re-run matches the slides already on the post.
	const [anchor, setAnchor] = React.useState<IBrandingAnchor | undefined>(undefined);
	const runIdRef = React.useRef(0);

	const replaceMediaRef = React.useRef(replaceMedia);
	React.useEffect(() => {
		replaceMediaRef.current = replaceMedia;
	}, [replaceMedia]);

	const versionsRef = React.useRef(versions);
	React.useEffect(() => {
		versionsRef.current = versions;
	}, [versions]);

	const conversationIdRef = React.useRef<string | undefined>(undefined);
	const conversationPromiseRef = React.useRef<Promise<string | undefined> | null>(null);
	const impersonatedAccountId = impersonationContext?.account?.id;
	const impersonatedUserId = impersonationContext?.user?.id;
	React.useEffect(() => {
		conversationIdRef.current = undefined;
	}, [impersonatedAccountId, impersonatedUserId]);
	const { mutateAsync: createConversation } = useAIAssistantCreateConversationMutation({
		onError: error => {
			logger.logApiError('brandPostMedia-conversation', error);
			toast.error('Failed to create conversation');
		},
	});

	const ensureConversation = React.useCallback(() => {
		if (conversationIdRef.current) {
			return Promise.resolve(conversationIdRef.current);
		}
		conversationPromiseRef.current ??= createConversation({
			assistantName: IMAGE_BRANDING_ASSISTANT_NAME,
			impersonationContext,
		})
			.then(conversation => {
				conversationIdRef.current = conversation?.id;
				return conversation?.id;
			})
			.finally(() => {
				conversationPromiseRef.current = null;
			});
		return conversationPromiseRef.current;
	}, [createConversation, impersonationContext]);

	const jobQuery = useGetSystemJobQuery<BrandingSystemJobResult>({
		enabled: !!run?.jobId && run.state === 'running',
		jobId: run?.jobId ?? '',
		refetchInterval: query => (isSystemJobFinished(query.state.data) ? false : JOB_POLL_INTERVAL_MS),
	});
	const job = run?.state === 'running' ? jobQuery.data : undefined;

	const finishedRows = isSystemJobFinished(job) ? (job.resultImages ?? []) : [];
	const resultAttachmentQueries = useMultipleAttachmentsQuery({
		enabled: finishedRows.length > 0,
		ids: finishedRows.map(row => row.fileAttachmentId),
		impersonationContext,
	});
	const settledResultKey = finishedRows
		.map(
			(row, index) =>
				`${row.fileAttachmentId}:${resultAttachmentQueries[index]?.isError ? 'x' : resultAttachmentQueries[index]?.data ? 'y' : '-'}`
		)
		.join(',');

	const swappedInResults = React.useRef<Set<string>>(new Set());
	React.useEffect(() => {
		if (!run || !isRunInFlight(run)) {
			return;
		}
		if (jobQuery.isError) {
			setRun(current =>
				current?.runId === run.runId ? { ...current, outcome: { failedImages: 0 }, state: 'failed' } : current
			);
			return;
		}
		if (!isSystemJobFinished(job)) {
			return;
		}
		const rows = finishedRows.map((row, index) => ({ ...row, query: resultAttachmentQueries[index] }));
		const ready = rows.filter(
			row => row.query?.data && !swappedInResults.current.has(`${run.jobId}:${row.fileAttachmentId}`)
		);
		if (ready.length) {
			const brandedBySource: Record<string, Api.IFileAttachment> = {};
			ready.forEach(row => {
				swappedInResults.current.add(`${run.jobId}:${row.fileAttachmentId}`);
				brandedBySource[row.sourceFileAttachmentId] = row.query.data;
			});
			versionsRef.current.forEach(version =>
				replaceMediaRef.current(media => {
					const next = media.map(attachment => brandedBySource[attachment.id] ?? attachment);
					return next.some((attachment, index) => attachment !== media[index]) ? next : media;
				}, version.platform)
			);
			setRun(current =>
				current?.runId === run.runId
					? { ...current, brandedBySource: { ...current.brandedBySource, ...brandedBySource } }
					: current
			);
		}
		if (rows.every(row => row.query?.data || row.query?.isError)) {
			const brandedRows = rows.filter(row => row.query?.data);
			const brandedCount = brandedRows.length;
			const failedImages = (job.metadata?.failedImages ?? 0) + (rows.length - brandedCount);
			if (brandedCount && run.jobId) {
				setAnchor({
					imageIds: [
						...(run.retry?.keptResultFileAttachmentIds ?? []),
						...brandedRows.map(row => row.fileAttachmentId),
					],
					jobId: run.jobId,
					templateId: run.templateId,
				});
			}
			setRun(current =>
				current?.runId === run.runId
					? { ...current, outcome: { failedImages }, state: brandedCount ? 'done' : 'failed' }
					: current
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [job, jobQuery.isError, run, settledResultKey]);

	useJobStallTimeout({
		enabled: isRunInFlight(run),
		fingerprint: `${run?.runId}:${run?.jobId}:${getSystemJobProgressFingerprint(job)}`,
		onStalled: () =>
			setRun(current =>
				isRunInFlight(current) ? { ...current, outcome: { failedImages: 0 }, state: 'failed' } : current
			),
	});

	const startRun = React.useCallback(
		async (runId: number, sourceIds: string[], retry: BrandingRetry | undefined) => {
			const updateThisRun = (update: (current: IBrandingRun) => IBrandingRun) =>
				setRun(current => (current?.runId === runId && current.state === 'starting' ? update(current) : current));
			try {
				const conversationId = await ensureConversation();
				if (!conversationId) {
					throw new Error('No image assistant conversation');
				}
				const jobId = await startImageBrandingStream({
					attachmentIds: sourceIds,
					conversationId,
					impersonationContext,
					retry,
					templateId,
					userSession,
				});
				updateThisRun(current => ({ ...current, jobId, state: 'running' }));
			} catch (error) {
				logger.logApiError('brandPostMedia-stream', Api.asApiError(error));
				const keptImagesGone = !!retry && error instanceof Error && error.message === KEPT_IMAGES_UNAVAILABLE_MESSAGE;
				if (keptImagesGone) {
					setAnchor(current => withoutRejectedJob(current, retry.priorJobId));
				}
				updateThisRun(current => ({
					...current,
					anchorBefore: keptImagesGone
						? withoutRejectedJob(current.anchorBefore, retry.priorJobId)
						: current.anchorBefore,
					outcome: { failedImages: 0 },
					state: 'failed',
				}));
			}
		},
		[ensureConversation, impersonationContext, logger, templateId, userSession]
	);

	const getBrandingState = (enabled: boolean): IBrandingState => {
		const brandedIds = new Set([
			...Object.values(run?.brandedBySource ?? {}).map(attachment => attachment.id),
			...(anchor?.imageIds ?? []),
		]);
		const postWideAttachments = getPostWideAttachments(versions);
		const imageIdsToBrand =
			getBrandingBatchForAttachments(postWideAttachments.filter(attachment => !brandedIds.has(attachment.id)))
				?.attachmentIds ?? [];
		const keptImageIds = [
			...new Set(
				postWideAttachments
					.map(attachment => attachment.id)
					.filter(id => anchor?.imageIds.includes(id) && !imageIdsToBrand.includes(id))
			),
		];
		const retry: BrandingRetry | undefined =
			anchor?.jobId && anchor.templateId === templateId && keptImageIds.length
				? { keptResultFileAttachmentIds: keptImageIds, priorJobId: anchor.jobId }
				: undefined;
		const isBranding = isRunInFlight(run);
		const isRunCurrent = !!run && isUntouchedSince(versions, run);
		const hasBrandedMedia = postWideAttachments.some(attachment => brandedIds.has(attachment.id));

		return {
			allImagesBranded: enabled && !imageIdsToBrand.length && hasBrandedMedia,
			brandingImageIds: new Set(isBranding ? (run?.sourceIds ?? []) : []),
			apply: () => {
				if (!imageIdsToBrand.length) {
					return;
				}
				const runId = ++runIdRef.current;
				setRun(current => ({
					anchorBefore: isRunCurrent && current ? current.anchorBefore : anchor,
					brandedBySource: isRunCurrent ? (current?.brandedBySource ?? {}) : {},
					runId,
					snapshot:
						isRunCurrent && current
							? current.snapshot
							: versions.map(version => ({ ...version, attachments: [...version.attachments] })),
					retry,
					sourceIds: imageIdsToBrand,
					state: 'starting',
					templateId,
				}));
				void startRun(runId, imageIdsToBrand, retry);
			},
			canApply: enabled && !isBranding && imageIdsToBrand.length > 0,
			canUndo: isRunCurrent && hasBrandedMedia && !isBranding,
			cancel: () => setRun(current => (current ? { ...current, state: 'cancelled' } : current)),
			failedImages: isRunCurrent ? (run?.outcome?.failedImages ?? 0) : 0,
			hasFailed: run?.state === 'failed' && isRunCurrent,
			isBranding,
			progressText: getBrandingProgressText({
				completedImages: (job?.metadata?.successfulImages ?? 0) + (job?.metadata?.failedImages ?? 0),
				currentImageAttempt: job?.metadata?.currentImageAttempt,
				currentImageNumber: job?.metadata?.currentImageNumber,
				currentPhase: job?.metadata?.currentPhase,
				hasJob: !!job?.status,
				isFinished: isSystemJobFinished(job),
				totalImages: job?.metadata?.totalImages || run?.sourceIds.length || 0,
			}),
			undo: () => {
				if (!run) {
					return;
				}
				run.snapshot.forEach(version =>
					replaceMediaRef.current(
						media =>
							media.length === version.attachments.length &&
							media.every((image, index) => image === version.attachments[index])
								? media
								: version.attachments,
						version.platform
					)
				);
				setAnchor(run.anchorBefore);
				setRun(undefined);
			},
		};
	};

	return {
		getBrandingState,
		isBranding: isRunInFlight(run),
		tabActivity: ((): BrandingTabActivity => {
			if (isRunInFlight(run)) {
				return 'branding';
			}
			if (run?.state === 'failed') {
				return 'failed';
			}
			return run?.state === 'done' ? 'done' : 'off';
		})(),
	};
}
